import React, { useState, useEffect } from 'react';

import Modal from 'components/Common/Modal';
import NumberInput from 'components/NumberInput';
import Button from 'components/Button';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import styles from './EditTradelinePayoffAmountModal.module.scss';

export interface EditTradelinePayoffAmountModalProps {
  isOpen: boolean;
  firm: string;
  balance: number;
  initialAmount: number;
  maxLoanAmount: number;
  currentTotalConsolidated: number;
  onClose: () => void;
  onUpdate: (amount: number) => void;
}

const getOverage = (currentTotalConsolidated: number, maxLoanAmount: number, paymentAmount: number) => {
  const newTotal = currentTotalConsolidated + paymentAmount;
  return newTotal - maxLoanAmount;
};

const isOnLoanAmountBudget = (overage: number) => overage <= 0;

const EditTradelinePayoffAmountModal = ({
  isOpen,
  firm,
  balance,
  initialAmount,
  maxLoanAmount,
  currentTotalConsolidated,
  onClose,
  onUpdate,
}: EditTradelinePayoffAmountModalProps) => {
  const [paymentAmount, setPaymentAmount] = useState(initialAmount.toString());
  const [errorMessage, setErrorMessage] = useState<string>('');

  const parsedAmount = parseFloat(paymentAmount.replace(/[$,]/g, '')) || 0;
  const overage = getOverage(currentTotalConsolidated, maxLoanAmount, parsedAmount);
  const isWithinBudget = isOnLoanAmountBudget(overage);

  useEffect(() => {
    if (!isWithinBudget) {
      setErrorMessage(`Reduce payment by ${formatMonetaryAmount(overage)} to stay within your loan amount.`);
    } else {
      setErrorMessage('');
    }
  }, [paymentAmount, currentTotalConsolidated, maxLoanAmount, isWithinBudget, overage]);

  if (!isOpen) return null;

  const handleUpdate = () => {
    onUpdate(isWithinBudget ? parsedAmount : maxLoanAmount - currentTotalConsolidated);
  };

  return (
    <Modal closeModal={onClose} usePortal>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderInfo}>
            <p className={styles.modalTitle}>{firm}</p>
            <p className={styles.modalSubtitle}>{formatMonetaryAmount(balance)} balance</p>
          </div>
        </div>

        <div>
          <NumberInput
            errorMessage={errorMessage}
            label="How much do you want Plannery to pay to this account?"
            prefix="$"
            thousandSeparator
            value={paymentAmount}
            placeholder="$"
            onChange={(e) => setPaymentAmount(e.target.value)}
          />
        </div>

        <Button onClick={handleUpdate} disabled={!isWithinBudget}>
          UPDATE
        </Button>

        <p className={styles.modalDisclaimer}>Plannery is not liable for any errors in edited debt payment amounts.</p>
      </div>
    </Modal>
  );
};

export default EditTradelinePayoffAmountModal;
